import { useState, useEffect } from "react";
import { useAuth } from "../useAuth";
import { BookOpen } from "lucide-react";
import { T, ALL_LABELS } from "../lib/i18n";
import { friendlyError, buildGroupCodes, parseGroupList, sortGroups, tooLongGroupNames, copyToClipboard, openCodeSheet, joinUrl } from "../lib/helpers";
import { stripFormatting } from "../lib/formatting";
import { dbCreateSessionWithFreshIds, dbLoadTeacherSessions, dbLoadUserCourses, dbLoadUserCourseList, dbEnsureCourseInstance, dbLoadQuestionBank, dbSaveQuestionToBank, dbLoadTags } from "../lib/db";
import { LabelText, QRDisplay, QuestionImages, RichText } from "../components/common";
import { RichTextField } from "../components/RichTextField";
import { LoginScreen } from "./LoginScreen";
import { TeacherSessionList } from "./TRATEditor";
import { ImportTab } from "./ImportTab";

// ─────────────────────────────────────────
//  TEACHER SETUP
// ─────────────────────────────────────────
// ─────────────────────────────────────────
//  BANK IMPORT TAB (used in TeacherSetup)
// ─────────────────────────────────────────
function BankImportTab({ onImport, selectedCourseInstanceId, myCourseInstances, lang, G, css }) {
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterTag, setFilterTag] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const labels = ["A", "B", "C", "D", "E", "F"];

  useEffect(() => {
    if (!user) return;
    Promise.all([dbLoadQuestionBank(user.id), dbLoadTags(user.id)]).then(([q, t]) => {
      setQuestions(q);
      setTags(t);
      setLoading(false);
    });
  }, [user]);

  const filtered = questions.filter(q => {
    const matchSearch = !search || stripFormatting(q.question_text).toLowerCase().includes(search.toLowerCase());
    const matchTag = !filterTag || (q.question_tags || []).some(qt => qt.tag_id === filterTag);
    return matchSearch && matchTag;
  });

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(filtered.map(q => q.id)));
  const clearAll = () => setSelected(new Set());

  const doImport = () => {
    const toImport = questions.filter(q => selected.has(q.id));
    if (toImport.length === 0) return;

    const ALL_LABELS = ["A", "B", "C", "D", "E", "F"];

    const mapped = toImport.map(q => ({
      text: q.question_text || "",
      rationale: q.explanation || "",
      options: q.options || {},
      // Figurernas URL:er följer med in i tRAT:en
      images: Array.isArray(q.images) ? q.images : [],
      correctAnswerLabel: q.correct_answer || "A",
      // Convert to index for answerKey
      correctAnswerIndex: ALL_LABELS.indexOf(q.correct_answer || "A"),
    }));

    onImport(mapped);
  };

  return (
    <div>
      {/* Info */}
      <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: "12px 14px", marginBottom: 16, fontSize: 13, color: G.muted, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 600, color: G.text, marginBottom: 4 }}>{lang === "sv" ? "Importera från frågebanken" : "Import from question bank"}</div>
        {lang === "sv" ? "Välj frågor nedan och klicka Importera. Frågorna läggs in i tRAT:en och kan granskas i fliken Frågor." : "Select questions below and click Import. They will be added to the tRAT and can be reviewed in the Questions tab."}
      </div>

      {/* Search & filter */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <input
          style={{ ...css.input, flex: 1, minWidth: 180, padding: "8px 12px", fontSize: 14 }}
          placeholder={lang === "sv" ? "Sök frågor…" : "Search questions…"}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Tag pills */}
      {tags.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
          <button onClick={() => setFilterTag(null)} style={{ padding: "3px 10px", borderRadius: 16, border: `1px solid ${!filterTag ? G.gold : G.border}`, background: !filterTag ? G.gold + "20" : "transparent", color: !filterTag ? G.gold : G.muted, fontSize: 12, cursor: "pointer" }}>
            {lang === "sv" ? "Alla" : "All"}
          </button>
          {tags.map(tag => (
            <button key={tag.id} onClick={() => setFilterTag(filterTag === tag.id ? null : tag.id)} style={{ padding: "3px 10px", borderRadius: 16, border: `1px solid ${filterTag === tag.id ? G.gold : G.border}`, background: filterTag === tag.id ? `${G.gold}20` : "transparent", color: filterTag === tag.id ? G.gold : G.muted, fontSize: 12, cursor: "pointer" }}>
              {tag.name}
            </button>
          ))}
        </div>
      )}

      {/* Select all / clear */}
      {filtered.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <button onClick={selectAll} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 12, padding: "3px 10px", cursor: "pointer" }}>
            {lang === "sv" ? "Markera alla" : "Select all"}
          </button>
          {selected.size > 0 && (
            <button onClick={clearAll} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 12, padding: "3px 10px", cursor: "pointer" }}>
              {lang === "sv" ? "Rensa" : "Clear"}
            </button>
          )}
        </div>
      )}

      {loading && <p style={{ color: G.muted, textAlign: "center", padding: 24 }}>{lang === "sv" ? "Laddar…" : "Loading…"}</p>}

      {!loading && filtered.length === 0 && (
        <div style={{ textAlign: "center", color: G.muted, padding: 32, background: G.surface2, borderRadius: 12 }}>
          <p style={{ fontSize: 14 }}>{questions.length === 0 ? (lang === "sv" ? "Frågebanken är tom." : "Question bank is empty.") : (lang === "sv" ? "Inga frågor matchar." : "No questions match.")}</p>
        </div>
      )}

      {/* Question list */}
      <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
        {filtered.map(q => {
          const isSelected = selected.has(q.id);
          const qTags = (q.question_tags || []).map(qt => qt.tags).filter(Boolean);
          return (
            <div
              key={q.id}
              onClick={() => toggleSelect(q.id)}
              style={{
                background: isSelected ? `${G.gold}10` : G.surface,
                border: `1px solid ${isSelected ? G.gold : G.border}`,
                borderRadius: 12,
                padding: "12px 16px",
                cursor: "pointer",
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
                transition: "all 0.15s",
              }}
            >
              {/* Checkbox */}
              <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${isSelected ? G.gold : G.border}`, background: isSelected ? G.gold : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2, transition: "all 0.15s" }}>
                {isSelected && <span style={{ color: G.bg, fontSize: 12, fontWeight: 700 }}>✓</span>}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Correct answer + question */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, fontWeight: 700, color: G.correct, background: G.correct + "20", borderRadius: 4, padding: "1px 6px", flexShrink: 0 }}>{q.correct_answer}</span>
                  <p style={{ margin: 0, fontSize: 14, color: G.text, lineHeight: 1.5, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                    {q.question_text ? <RichText text={q.question_text} /> : <span style={{ color: G.muted, fontStyle: "italic" }}>Ingen frågetext</span>}
                  </p>
                </div>

                {/* Tags */}
                {qTags.length > 0 && (
                  <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
                    {qTags.map(tag => (
                      <span key={tag.id} style={{ fontSize: 10, color: tag.color, background: tag.color + "20", border: `1px solid ${tag.color}40`, borderRadius: 8, padding: "1px 6px" }}>{tag.name}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Import button */}
      {selected.size > 0 && (
        <div style={{ position: "sticky", bottom: 0, padding: "12px 0", background: G.surface, borderTop: `1px solid ${G.border}`, marginTop: 8 }}>
          <button
            onClick={doImport}
            style={{ ...css.btnPrimary, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
          >
            {lang === "sv" ? `Importera ${selected.size} fråga${selected.size !== 1 ? "r" : ""}` : `Import ${selected.size} question${selected.size !== 1 ? "s" : ""}`} →
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────
//  TEACHER SETUP
// ─────────────────────────────────────────
function TeacherSetup({ onCreated, onBack, lang, setLang, theme, setTheme, preselectedCourse, G, css }) {
  const [numQ, setNumQ] = useState(15);
  const [numOptions, setNumOptions] = useState(4);
  const [answerKey, setAnswerKey] = useState(Array(15).fill(0));
  const [questions, setQuestions] = useState(Array(15).fill(null).map(() => ({ text: "", rationale: "", options: {} })));
  const [showQTexts, setShowQTexts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sessionTitle, setSessionTitle] = useState("");
  const [scheduleMode, setScheduleMode] = useState("now"); // "now" or "scheduled"
  const [availableAt, setAvailableAt] = useState("");
  const [predefinedGroupsText, setPredefinedGroupsText] = useState("");
  const [groupMode, setGroupMode] = useState("predefined"); // "free" | "predefined"
  const [groupGenCount, setGroupGenCount] = useState(8);
  const [groupGenPrefix, setGroupGenPrefix] = useState("Grupp");
  const [randomizeQuestions, setRandomizeQuestions] = useState(false);
  const [randomizeOptions, setRandomizeOptions] = useState(false);
  const [sessionDuration, setSessionDuration] = useState(""); // minutes, empty = no limit
  const [selectedCourseInstanceId, setSelectedCourseInstanceId] = useState(null);
  const [selectedCourseInstanceObj, setSelectedCourseInstanceObj] = useState(null);
  const [myCourseInstances, setMyCourseInstances] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [savedToBank, setSavedToBank] = useState({}); // qi -> true
  const [createErr, setCreateErr] = useState(null);
  const { user } = useAuth();
  const t = T[lang];
  const LABELS = ALL_LABELS.slice(0, numOptions);
  const [tab, setTab] = useState("create"); // "create" | "sessions"
  // Wizard: "course" → "instance" → "create"
  const [wizardStep, setWizardStep] = useState("create");
  const [selectedCourse, setSelectedCourse] = useState(preselectedCourse || null);

  const ACCENTS_W = ["#60a5fa", "#34d399", "#a78bfa", "#f97316", "#f59e0b", "#ec4899"];
  function accentForW(id) {
    let h = 0;
    for (let i = 0; i < (id || "").length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    return ACCENTS_W[h % ACCENTS_W.length];
  }

  // Läraren väljer bara kurs. Terminens tillfälle hämtas (eller skapas) av
  // servern utifrån dagens datum, så ingen behöver administrera tillfällen.
  const [resolvingCourse, setResolvingCourse] = useState(false);
  async function handleCourseClick(course) {
    setSelectedCourse(course);
    setResolvingCourse(true);
    const inst = await dbEnsureCourseInstance(course.id);
    setResolvingCourse(false);
    if (!inst) {
      setCreateErr(lang === "sv"
        ? "Kunde inte koppla tRAT:en till kursens termin. Försök igen."
        : "Could not link the tRAT to the course term. Please try again.");
      return;
    }
    setSelectedCourseInstanceId(inst.id);
    setSelectedCourseInstanceObj({ ...inst, courses: course });
    setWizardStep("create");
  }

  useEffect(() => {
    if (!user) return;
    Promise.all([dbLoadUserCourses(user.id), dbLoadUserCourseList(user.id)])
      .then(([instances, courses]) => {
        setMyCourseInstances(instances);
        setMyCourses(courses);
        // Varje tRAT tillhör en kurs.
        //  • förvald (kom från en kurssida) → behåll den
        //  • exakt en kurs → välj den automatiskt
        //  • flera kurser → låt läraren välja kurs först
        //  • ingen kurs → skapa utan koppling (läraren inte kopplad än)
        if (preselectedCourse?.id) {
          // Kom från en kurssida — lös terminen för just den kursen
          if (!selectedCourseInstanceId) handleCourseClick(preselectedCourse);
        } else if (courses.length === 1) {
          handleCourseClick(courses[0]);
        } else if (courses.length > 1 && !selectedCourseInstanceId) {
          setWizardStep("course");
        }
      });
  }, [user]);

  async function saveQuestionToBank(qi) {
    if (!user) return false;
    const q = questions[qi];
    if (!q?.text?.trim()) return false; // never save an empty question
    const correctAnswer = ALL_LABELS[answerKey[qi]];
    // Find course/program/org from selected course instance
    const inst = myCourseInstances.find(ci => ci.id === selectedCourseInstanceId);
    const courseId = inst?.course_id || null;
    const programId = inst?.courses?.program_id || null;
    const orgId = inst?.courses?.programs?.org_id || null;
    const saved = await dbSaveQuestionToBank(
      // images följer med så figurfrågor inte tappar sitt innehåll i banken
      { text: q.text, options: q.options, correctAnswer, rationale: q.rationale, images: q.images },
      user.id, selectedCourseInstanceId, courseId, programId, orgId
    );
    // Only confirm when the write actually succeeded
    if (saved) { setSavedToBank(prev => ({ ...prev, [qi]: true })); return true; }
    return false;
  }

  async function saveAllQuestionsToBank() {
    for (let i = 0; i < questions.length; i++) {
      if (questions[i]?.text?.trim()) await saveQuestionToBank(i);
    }
  }
  // Flikarna ligger i arbetsordning: namnge och schemalägg → hämta in frågor →
  // kontrollera facit. Titeln är det första man vill sätta, så starta där.
  const [createTab, setCreateTab] = useState("settings"); // "settings" | "questions"
  // Frågefliken har två lägen: importera (tom tRAT) och redigera (frågor finns).
  // Import och facit var förut två flikar bredvid varandra, vilket gjorde det
  // oklart vad som var vad. Nu är importen vägen IN till frågorna, och
  // "Importera om" vägen tillbaka — samma modell som editorn för en befintlig tRAT.
  const [qMode, setQMode] = useState("choose"); // "choose" | "import" | "edit"
  // Var frågorna kommer ifrån styr vilken redigeringsvy som passar: importerade
  // och egenskrivna frågor visas som kort (Ta bort / Lägg till fråga), där antalet
  // följer korten; "bara facit" har antalsfält och ett rutnät med rätt bokstav.
  const [showBank, setShowBank] = useState(false);
  const [qSource, setQSource] = useState(null); // "imported" | "written" | "manual" | null
  const withCards = qSource === "imported" || qSource === "written";
  function removeQuestion(qi) {
    setQuestions(prev => prev.filter((_, i) => i !== qi));
    setAnswerKey(prev => prev.filter((_, i) => i !== qi));
    setNumQ(n => Math.max(1, n - 1));
  }
  function addQuestion() {
    setQuestions(prev => [...prev, { text: "", rationale: "", options: {} }]);
    setAnswerKey(prev => [...prev, 0]);
    setNumQ(n => n + 1);
  }
const [mySessions, setMySessions] = useState([]);

async function loadMySessions() {
  if (!user) return;
  const sessions = await dbLoadTeacherSessions(user.id);
  setMySessions(sessions);
}

useEffect(() => {
  if (tab === "sessions") {
    loadMySessions();
  }
}, [tab, user]);

  function handleNumQ(n) {
    const nn = Math.max(1, Math.min(50, parseInt(n) || 1));
    setNumQ(nn);
    setAnswerKey(prev => { const next = [...prev]; while (next.length < nn) next.push(0); return next.slice(0, nn).map(v => Math.min(v, numOptions - 1)); });
    setQuestions(prev => { const next = [...prev]; while (next.length < nn) next.push({ text: "", rationale: "", options: {} }); return next.slice(0, nn); });
  }

  function handleNumOptions(n) {
    const nn = Math.max(2, Math.min(6, parseInt(n) || 4));
    setNumOptions(nn);
    setAnswerKey(prev => prev.map(v => Math.min(v, nn - 1)));
  }

  function setQ(qi, field, val) {
    setQuestions(prev => { const next = [...prev]; next[qi] = { ...next[qi], [field]: val }; return next; });
  }

  // "Grupp 1 … Grupp 16" i ett klick. Lägger till efter det som redan står i
  // rutan och hoppar över namn som redan finns, så knappen inte kan råka
  // radera en handskriven lista eller skapa dubbletter.
  function generateGroupNames() {
    const n = Math.max(1, Math.min(60, parseInt(groupGenCount) || 0));
    const prefix = groupGenPrefix.trim();
    const existing = parseGroupList(predefinedGroupsText);
    const seen = new Set(existing.map(l => l.toLowerCase()));
    const added = [];
    for (let i = 1; i <= n; i++) {
      const name = prefix ? `${prefix} ${i}` : String(i);
      if (seen.has(name.toLowerCase())) continue;
      seen.add(name.toLowerCase());
      added.push(name);
    }
    setPredefinedGroupsText(sortGroups([...existing, ...added]).join("\n"));
  }

  async function createSession() {
    setCreateErr(null);
    const predefinedGroups = groupMode === "predefined" ? sortGroups(parseGroupList(predefinedGroupsText)) : [];
    // Förhandsskapade grupper utan en enda grupp = en tRAT ingen kan ansluta till.
    // Kontrollen ligger FÖRE setSaving(true) — låg den efter fastnade knappen i
    // "Skapar…" när kontrollen stoppade.
    if (groupMode === "predefined" && predefinedGroups.length === 0) {
      setCreateErr(t.groupsRequired); setCreateTab("settings"); return;
    }
    const tooLong = tooLongGroupNames(predefinedGroups);
    if (tooLong.length > 0) {
      setCreateErr(t.groupNameTooLong(tooLong[0])); setCreateTab("settings"); return;
    }
    setSaving(true);
    const groupCodes = predefinedGroups.length > 0 ? buildGroupCodes(predefinedGroups, {}) : {};
    const dur = parseInt(sessionDuration);
    try {
      const session = await dbCreateSessionWithFreshIds(ids => ({
        ...ids, answerKey, numQ, numOptions, questions,
        sessionTitle: sessionTitle.trim() || null,
        // datetime-local is a naive local time — convert to ISO so Postgres
        // doesn't interpret it as UTC (a 14:00 tRAT used to start at 16:00)
        availableAt: scheduleMode === "scheduled" && availableAt ? new Date(availableAt).toISOString() : null,
        courseInstanceId: selectedCourseInstanceId || null,
        config: {
          ...(predefinedGroups.length > 0 ? { predefined_groups: predefinedGroups, group_codes: groupCodes } : {}),
          ...(dur > 0 ? { session_duration: dur } : {}),
          ...(randomizeQuestions ? { randomize_questions: true } : {}),
          ...(randomizeOptions ? { randomize_options: true } : {}),
        },
      }), user?.id);
      onCreated(session);
    } catch (e) {
      setCreateErr(friendlyError(e, lang));
    } finally {
      setSaving(false);
    }
  }

  if (!user) return <LoginScreen lang={lang} G={G} css={css} />;

  return (
    <div style={css.page}>
      <div style={{ display: "flex", justifyContent: "center", padding: "32px 20px 40px" }}>
        <div style={{ ...css.card, maxWidth: 700 }}>
          <h2 style={{ ...css.heading, fontSize: 26, marginBottom: 4 }}>{t.createSessionTitle}</h2>
          <p style={{ color: G.muted, fontSize: 14, marginBottom: 28 }}>{t.createSessionSub}</p>
          {/* Tabs */}
<div style={{ display: "flex", gap: 0, marginBottom: 28, borderBottom: `2px solid ${G.border}` }}>
  <button
    onClick={() => setTab("create")}
    style={{
      background: "transparent",
      color: tab === "create" ? G.text : G.muted,
      border: "none",
      borderBottom: tab === "create" ? `3px solid ${G.gold}` : "3px solid transparent",
      padding: "12px 24px", fontSize: 15,
      fontWeight: tab === "create" ? 700 : 500, 
      cursor: "pointer",
      transition: "all 0.2s",
      marginBottom: -2
    }}
  >
    {t.createSession}
  </button>
  <button
    onClick={() => setTab("sessions")}
    style={{
      background: "transparent",
      color: tab === "sessions" ? G.text : G.muted,
      border: "none",
      borderBottom: tab === "sessions" ? `3px solid ${G.gold}` : "3px solid transparent",
      padding: "12px 24px", fontSize: 15,
      fontWeight: tab === "sessions" ? 700 : 500,
      cursor: "pointer",
      transition: "all 0.2s",
      marginBottom: -2
    }}
  >
    {t.mySessions}
  </button>
</div>

{/* ── Wizard: välj kurs (enda steget — terminen sätts automatiskt) ── */}
{tab === "create" && wizardStep === "course" && myCourses.length > 0 && (() => {
  const uniqueCourses = myCourses;
  return (
    <div>
      <h3 style={{ ...css.heading, fontSize: 18, marginBottom: 6, color: G.text }}>
        {lang === "sv" ? "Vilken kurs gäller tRAT:en?" : "Which course is this tRAT for?"}
      </h3>
      <p style={{ color: G.muted, fontSize: 14, marginBottom: 20 }}>
        {lang === "sv" ? "Välj kursen din tRAT ska tillhöra." : "Choose the course this tRAT belongs to."}
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12, marginBottom: 20 }}>
        {uniqueCourses.map(course => {
          const accent = accentForW(course.id);
          const prog = course.programs;
          return (
            <button
              key={course.id}
              disabled={resolvingCourse}
              onClick={() => handleCourseClick(course)}
              style={{
                background: G.surface,
                border: `1px solid ${G.border}`,
                borderTop: `3px solid ${accent}`,
                borderRadius: 12,
                padding: "16px",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.2s",
                fontFamily: "'DM Sans',sans-serif",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.borderTopColor = accent; e.currentTarget.style.transform = "translateY(-2px)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = G.border; e.currentTarget.style.borderTopColor = accent; e.currentTarget.style.transform = "translateY(0)"; }}
            >
              <div style={{ fontSize: 14, fontWeight: 600, color: G.text, marginBottom: 4 }}>
                {course.name || "—"}
              </div>
              {prog && <div style={{ fontSize: 12, color: G.muted }}>{prog.name}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
})()}

{/* ── Wizard Step 3: Create form ── */}
{tab === "create" && (wizardStep === "create" || myCourses.length === 0) && (
  <>
    {/* Selected course header */}
    {selectedCourseInstanceObj && (
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: G.surface2, border: `1px solid ${G.border}`, borderRadius: 8,
        padding: "10px 14px", marginBottom: 20
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <BookOpen size={14} color={G.gold} />
          <span style={{ fontSize: 13, color: G.text, fontWeight: 500 }}>
            {selectedCourseInstanceObj.courses?.name || "—"} · {selectedCourseInstanceObj.name}{selectedCourseInstanceObj.term ? " " + selectedCourseInstanceObj.term : ""}{selectedCourseInstanceObj.year ? " " + selectedCourseInstanceObj.year : ""}
          </span>
        </div>
        {/* Only offer "change" when the teacher actually has more than one
            course instance — with a single course there's nothing to switch to. */}
        {myCourses.length > 1 && (
          <button
            onClick={() => {
              setSelectedCourseInstanceId(null);
              setSelectedCourseInstanceObj(null);
              setSelectedCourse(null);
              setWizardStep("course");
            }}
            style={{ background: "none", border: "none", color: G.muted, cursor: "pointer", fontSize: 13, fontFamily: "'DM Sans',sans-serif", display: "flex", alignItems: "center", gap: 4 }}
            onMouseEnter={e => e.currentTarget.style.color = G.text}
            onMouseLeave={e => e.currentTarget.style.color = G.muted}
          >
            ← {lang === "sv" ? "Byt kurs" : "Change course"}
          </button>
        )}
      </div>
    )}

    {/* Inner create tabs */}
    <div style={{ display: "flex", gap: 0, marginBottom: 32, borderBottom: `2px solid ${G.border}` }}>
      {[
        { id: "settings", label: t.settingsTab },
        { id: "questions", label: t.questionsTab },
      ].map(({ id, label }) => (
        <button
          key={id}
          onClick={() => setCreateTab(id)}
          style={{
            background: "transparent",
            color: createTab === id ? G.text : G.muted,
            border: "none",
            borderBottom: createTab === id ? `3px solid ${G.gold}` : "3px solid transparent",
            padding: "10px 20px", fontSize: 14,
            fontWeight: createTab === id ? 700 : 500, 
            cursor: "pointer",
            transition: "all 0.2s",
            marginBottom: -2
          }}
          onMouseEnter={(e) => {
            if (createTab !== id) {
              e.target.style.color = G.text;
              e.target.style.background = `${G.surface2}`;
            }
          }}
          onMouseLeave={(e) => {
            if (createTab !== id) {
              e.target.style.color = G.muted;
              e.target.style.background = "transparent";
            }
          }}
        >
          {label}
        </button>
      ))}
    </div>

    {createErr && (
      <div role="alert" style={{ background: G.red + "18", border: `1.5px solid ${G.red}55`, borderRadius: 12, padding: "12px 16px", marginBottom: 20, fontSize: 14, color: G.text }}>
        {createErr}
      </div>
    )}

    {/* Settings tab */}
    {createTab === "settings" && (
      <>
        {/* Session Title */}
        <div style={{ marginBottom: 24 }}>
          <label style={css.label}><LabelText text={t.sessionTitle} /></label>
          <input type="text" style={css.input} placeholder={t.sessionTitlePlaceholder} value={sessionTitle} onChange={e => setSessionTitle(e.target.value)} />
        </div>
        {/* Schedule Options */}
        <div style={{ marginBottom: 24 }}>
          <label style={css.label}>{t.sessionSchedule}</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
            <button onClick={() => setScheduleMode("now")} style={{ 
              background: scheduleMode === "now" ? G.surface2 : "transparent", 
              color: scheduleMode === "now" ? G.text : G.muted, 
              border: `1.5px solid ${scheduleMode === "now" ? G.gold : G.border}`, 
              borderRadius: 8, padding: "10px 12px", fontSize: 14, 
              fontWeight: scheduleMode === "now" ? 600 : 400, 
              cursor: "pointer",
              transition: "all 0.2s"
            }}>
              {t.makeAvailableNow}
            </button>
            <button onClick={() => setScheduleMode("scheduled")} style={{ 
              background: scheduleMode === "scheduled" ? G.surface2 : "transparent", 
              color: scheduleMode === "scheduled" ? G.text : G.muted, 
              border: `1.5px solid ${scheduleMode === "scheduled" ? G.gold : G.border}`, 
              borderRadius: 8, padding: "10px 12px", fontSize: 14, 
              fontWeight: scheduleMode === "scheduled" ? 600 : 400, 
              cursor: "pointer",
              transition: "all 0.2s"
            }}>
              {t.makeAvailableAt}
            </button>
          </div>
          {scheduleMode === "scheduled" && (
            <input type="datetime-local" style={css.input} value={availableAt} onChange={e => setAvailableAt(e.target.value)} />
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
            <label style={{ ...css.label, marginBottom: 0, whiteSpace: "nowrap" }}>{t.sessionDurationLabel}</label>
            <input type="number" min={1} max={180} style={{ ...css.input, width: 80 }} placeholder="—" value={sessionDuration} onChange={e => setSessionDuration(e.target.value)} />
            <span style={{ fontSize: 13, color: G.muted }}>{t.sessionDurationHint}</span>
          </div>
        </div>
        {/* Group registration mode — förvalet (förhandsskapade) ligger först,
            och varje alternativ förklarar sin konsekvens: koden. */}
        <div style={{ marginBottom: 24 }}>
          <label style={css.label}>{t.predefinedGroups}</label>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 8, marginBottom: 12 }}>
            {[
              { id: "predefined", label: t.groupModePredefined, desc: t.groupModePredefinedDesc, tip: t.groupModePredefinedTip },
              { id: "free", label: t.groupModeFree, desc: t.groupModeFreeDesc, tip: t.groupModeFreeTip },
            ].map(m => (
              <button key={m.id} onClick={() => setGroupMode(m.id)} title={m.tip} style={{
                background: groupMode === m.id ? G.surface2 : "transparent",
                color: groupMode === m.id ? G.text : G.muted,
                border: `1.5px solid ${groupMode === m.id ? G.gold : G.border}`,
                borderRadius: 8, padding: "10px 12px", fontSize: 14,
                fontWeight: groupMode === m.id ? 600 : 400,
                cursor: "pointer", textAlign: "left",
                transition: "all 0.2s"
              }}>
                <div style={{ marginBottom: 4 }}>{m.label}</div>
                <div style={{ fontSize: 12, fontWeight: 400, color: G.muted, lineHeight: 1.5 }}>{m.desc}</div>
              </button>
            ))}
          </div>
          {groupMode === "predefined" && (
            <div>
              <p style={{ color: G.muted, fontSize: 14, marginBottom: 10 }}>{t.predefinedGroupsHint}</p>
              {/* 16 grupper är inte ovanligt — att knappa in dem en och en är
                  onödigt. Namnen läggs till sist i listan, inget skrivs över. */}
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                <div>
                  <label style={{ ...css.label, fontSize: 10 }}>{t.groupGenCount}</label>
                  <input type="number" min={1} max={60} style={{ ...css.input, width: 80 }} value={groupGenCount} onChange={e => setGroupGenCount(e.target.value)} />
                </div>
                <div style={{ flex: "1 1 140px", minWidth: 120 }}>
                  <label style={{ ...css.label, fontSize: 10 }}>{t.groupGenPrefix}</label>
                  <input type="text" style={css.input} value={groupGenPrefix} onChange={e => setGroupGenPrefix(e.target.value)} />
                </div>
                <button onClick={generateGroupNames} style={{ background: G.gold + "18", border: `1px solid ${G.gold}55`, borderRadius: 8, color: G.gold, fontSize: 13, fontWeight: 600, padding: "9px 16px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", whiteSpace: "nowrap" }}>
                  {t.groupGenAdd}
                </button>
              </div>
              <textarea style={{ ...css.textarea, minHeight: 120, fontFamily: "'DM Mono',monospace", fontSize: 14 }} placeholder={t.predefinedGroupsPlaceholder} value={predefinedGroupsText} onChange={e => setPredefinedGroupsText(e.target.value)} />
            </div>
          )}
        </div>
        {/* Slumpad ordning — av som standard. Slumpen är ren presentation:
            poäng, facit och appeals räknar alltid i kanonisk ordning. */}
        <div style={{ marginBottom: 24 }}>
          <label style={css.label}>{t.randomizeLabel}</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
            {[
              { on: randomizeQuestions, set: setRandomizeQuestions, label: t.randomizeQuestions, desc: t.randomizeQuestionsDesc },
              { on: randomizeOptions, set: setRandomizeOptions, label: t.randomizeOptions, desc: t.randomizeOptionsDesc },
            ].map((o, i) => (
              <label key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, background: o.on ? G.gold + "12" : G.bg, border: `1.5px solid ${o.on ? G.gold + "66" : G.border}`, borderRadius: 10, padding: "10px 14px", cursor: "pointer", fontSize: 14, color: G.text, transition: "all 0.15s" }}>
                <input
                  type="checkbox"
                  checked={o.on}
                  onChange={e => o.set(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: G.gold, flexShrink: 0, marginTop: 2 }}
                />
                <span>
                  <span style={{ fontWeight: 500 }}>{o.label}</span>
                  <span style={{ display: "block", fontSize: 12, color: G.muted, lineHeight: 1.5, marginTop: 2 }}>{o.desc}</span>
                </span>
              </label>
            ))}
          </div>
          <p style={{ color: G.muted, fontSize: 13, lineHeight: 1.6, margin: 0 }}>{t.randomizeHint}</p>
        </div>
      </>
    )}

    {/* Frågor & facit — första valet: var kommer frågorna ifrån? */}
    {createTab === "questions" && qMode === "choose" && (
      <div>
        <h3 style={{ ...css.heading, fontSize: 18, marginBottom: 16, color: G.text }}>{t.qChooseTitle}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12 }}>
          {[
            { id: "import", title: t.qChooseImport, desc: t.qChooseImportDesc, onPick: () => setQMode("import") },
            // Egna frågor börjar med ett tomt kort; fler läggs till ett i taget
            { id: "write", title: t.qChooseWrite, desc: t.qChooseWriteDesc, onPick: () => {
              setQuestions([{ text: "", rationale: "", options: {} }]); setAnswerKey([0]); setNumQ(1);
              setQSource("written"); setShowQTexts(true); setQMode("edit");
            } },
            { id: "manual", title: t.qChooseManual, desc: t.qChooseManualDesc, onPick: () => { setQSource("manual"); setShowQTexts(false); setQMode("edit"); } },
          ].map(o => (
            <button key={o.id} onClick={o.onPick} style={{ background: G.surface, border: `1.5px solid ${G.border}`, borderRadius: 12, padding: "18px 18px", textAlign: "left", cursor: "pointer", transition: "border-color 0.15s", display: "flex", flexDirection: "column", justifyContent: "flex-start" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = G.gold; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = G.border; }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: G.text, marginBottom: 6 }}>{o.title}</div>
              <div style={{ fontSize: 13, color: G.muted, lineHeight: 1.5 }}>{o.desc}</div>
            </button>
          ))}
        </div>
      </div>
    )}

    {/* Answer Key & Questions tab */}
    {createTab === "questions" && qMode === "edit" && (
      <>
        {/* Vägen tillbaka till importen — ersätter hela frågesattet, som i editorn */}
        <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: "12px 16px", marginBottom: 20, fontSize: 14, color: G.muted, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span>{qSource === "imported" ? t.reimportBarImported.replace("{n}", questions.length) : qSource === "written" ? t.writeBar : t.reimportBar}</span>
          <button
            onClick={() => setQMode("import")}
            style={{ background: G.gold + "18", border: `1px solid ${G.gold}55`, borderRadius: 8, color: G.gold, fontSize: 13, fontWeight: 600, padding: "5px 14px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
          >
            {qSource === "imported" ? t.tabReimport : t.importTab}
          </button>
        </div>
        {qSource !== "imported" && (
        <div style={{ display: "flex", gap: 20, marginBottom: 24, flexWrap: "wrap" }}>
          {/* Egna frågor: antalet följer korten, bara alternativantalet väljs */}
          {qSource === "manual" && <div><label style={css.label}>{t.numQuestions}</label><input type="number" min={1} max={50} style={{ ...css.input, width: 90 }} value={numQ} onChange={e => handleNumQ(e.target.value)} /></div>}
          <div><label style={css.label}>{t.numOptions}</label><input type="number" min={2} max={6} style={{ ...css.input, width: 90 }} value={numOptions} onChange={e => handleNumOptions(e.target.value)} /></div>
        </div>
        )}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <label style={{ ...css.label, marginBottom: 0 }}>{t.answerKey}</label>
          <button
            onClick={() => setShowQTexts(p => !p)}
            style={{ background: showQTexts ? G.gold + "18" : "transparent", border: `1px solid ${showQTexts ? G.gold : G.border}`, borderRadius: 8, color: showQTexts ? G.gold : G.muted, fontSize: 13, padding: "5px 14px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 500, transition: "all 0.2s" }}
          >
            {showQTexts ? (lang === "sv" ? "▲ Dölj frågor" : "▲ Hide questions") : (lang === "sv" ? "▼ Visa fråga" : "▼ Show questions")}
          </button>
        </div>

        {!showQTexts ? (
          /* Compact answer key grid — auto-fill so it doesn't cram on phones */
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(92px, 1fr))", gap: 8, marginBottom: 28 }}>
            {answerKey.map((v, qi) => (
              <div key={qi} style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 12, padding: "10px 8px", textAlign: "center" }}>
                <div style={{ ...css.heading, fontSize: 14, color: G.text, marginBottom: 8 }}>Q{qi + 1}</div>
                <select style={{ background: "transparent", border: "none", borderBottom: `1px solid ${G.goldDim}`, color: G.gold, fontFamily: "'DM Mono',monospace", fontSize: 17, fontWeight: 500, textAlign: "center", outline: "none", cursor: "pointer", width: "100%", padding: "2px 0" }}
                  value={v} onChange={e => { const n = [...answerKey]; n[qi] = parseInt(e.target.value); setAnswerKey(n); }}>
                  {LABELS.map((l, i) => <option key={i} value={i} style={{ background: G.surface }}>{l}</option>)}
                </select>
              </div>
            ))}
          </div>
        ) : (
          /* Expanded: answer key + question details */
          <div style={{ marginBottom: 16 }}>
            {qSource === "manual" && (
            <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, padding: "12px 14px", marginBottom: 16, fontSize: 13, color: G.muted, lineHeight: 1.6 }}>
              <div style={{ fontWeight: 600, color: G.text, marginBottom: 6 }}>{lang === 'sv' ? 'Frågeinnehåll (valfritt)' : 'Question content (optional)'}</div>
              {lang === 'sv' ? (
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  <li>Du behöver inte skriva in frågorna här om du redan har dem på papper eller skärm. Det räcker att du fyller i facit för att de digitala skraplotterna ska fungera.</li>
                  <li style={{ marginTop: 4 }}>Återkoppling till rätt svar visas för studenterna <strong style={{ color: G.text }}>endast om läraren aktiverar det</strong> i dashboarden, t ex efter genomförd tRAT.</li>
                </ul>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  <li>Question texts and answer options are optional. If questions are distributed another way (paper, LMS etc.), only the answer key is needed to use the digital scratch cards.</li>
                  <li style={{ marginTop: 4 }}>Rationales for correct answers are shown to students <strong style={{ color: G.text }}>only if enabled by the teacher</strong> in the dashboard, e.g. after tRAT completion.</li>
                </ul>
              )}
            </div>
            )}
            {questions.map((q, qi) => (
              <div key={qi} style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                  <div style={{ ...css.heading, fontSize: 14, color: G.gold }}>Q{qi + 1}</div>
                  {withCards && questions.length > 1 && (
                    <button onClick={() => removeQuestion(qi)} title={t.removeQuestion} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 7, color: G.muted, fontSize: 12, padding: "3px 10px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
                      {t.removeQuestion}
                    </button>
                  )}
                </div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ ...css.label, fontSize: 10, marginBottom: 4 }}>{t.questionText}</label>
                  <RichTextField value={q?.text || ""} onChange={v => setQ(qi, "text", v)} ariaLabel={`Q${qi + 1} ${t.questionText}`} minHeight={60} G={G} css={css} lang={lang} />
                </div>
                {/* Figurer följer med från PDF-importen och sparas med tRAT:en */}
                {q?.images?.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <label style={{ ...css.label, fontSize: 10, marginBottom: 4, color: G.accent }}>
                      {lang === "sv" ? `Figur (${q.images.length})` : `Figure (${q.images.length})`}
                    </label>
                    <QuestionImages images={q.images} G={G} lang={lang} maxHeight={220} />
                  </div>
                )}
                <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 }}>
                  <label style={{ ...css.label, fontSize: 10, marginBottom: 2, color: G.goldDim }}>{lang === 'sv' ? 'Svarsalternativ' : 'Answer options'}<span style={{ color: G.muted, fontWeight: 400, marginLeft: 6 }}>({lang === 'sv' ? 'klicka på bokstav för att sätta rätt svar' : 'click letter to set correct answer'})</span></label>
                  {ALL_LABELS.slice(0, numOptions).map(lbl => {
                    const isCorrect = lbl === ALL_LABELS[answerKey[qi]];
                    return (
                      <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <button
                          type="button"
                          onClick={() => { setAnswerKey(prev => { const n = [...prev]; n[qi] = ALL_LABELS.indexOf(lbl); return n; }); }}
                          title={lang === 'sv' ? 'Markera som rätt svar' : 'Mark as correct answer'}
                          aria-pressed={isCorrect}
                          aria-label={`Q${qi + 1} ${lbl}${lang === 'sv' ? ' – rätt svar' : ' – correct answer'}`}
                          style={{ fontFamily: "'DM Mono',monospace", fontSize: 13, fontWeight: 600, minWidth: 24, textAlign: "center", padding: "2px 5px", borderRadius: 6, border: "none", cursor: "pointer", color: isCorrect ? G.correct : G.goldDim, background: isCorrect ? G.correct + "20" : G.surface, transition: "all 0.15s" }}
                        >
                          {lbl}{isCorrect ? " ✓" : ""}
                        </button>
                        <input style={{ ...css.input, fontSize: 14, padding: "7px 10px", flex: 1 }} value={q?.options?.[lbl] || ""} onChange={e => setQ(qi, "options", { ...(q?.options || {}), [lbl]: e.target.value })} />
                      </div>
                    );
                  })}
                </div>
                <div>
                  <label style={{ ...css.label, fontSize: 10, marginBottom: 4, color: G.goldDim }}>{t.rationaleText}</label>
                  <RichTextField value={q?.rationale || ""} onChange={v => setQ(qi, "rationale", v)} ariaLabel={`Q${qi + 1} ${t.rationaleText}`} minHeight={60} G={G} css={css} lang={lang} />
                </div>
                {/* Save to bank button */}
                {user && (
                  <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
                    <button
                      onClick={() => saveQuestionToBank(qi)}
                      disabled={savedToBank[qi]}
                      style={{ background: "none", border: `1px solid ${savedToBank[qi] ? G.correct : G.border}`, borderRadius: 8, color: savedToBank[qi] ? G.correct : G.muted, fontSize: 12, padding: "4px 12px", cursor: savedToBank[qi] ? "default" : "pointer", fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", gap: 4 }}
                    >
                      {savedToBank[qi] ? "✓ Sparad i banken" : "Spara till frågebank"}
                    </button>
                  </div>
                )}
              </div>
            ))}
            {withCards && (
              <button onClick={addQuestion} style={{ ...css.btnSecondary, marginTop: 4 }}>{t.addQuestion}</button>
            )}
            {/* Save all button */}
            {user && questions.some(q => q?.text?.trim()) && (
              <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={saveAllQuestionsToBank}
                  style={{ background: "none", border: `1px solid ${G.gold}`, borderRadius: 8, color: G.gold, fontSize: 13, padding: "6px 16px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
                >
                  {lang === "sv" ? "Spara alla till frågebank" : "Save all to question bank"}
                </button>
              </div>
            )}
          </div>
        )}
      </>
    )}

    {/* Import tab (includes file import + question bank) */}
    {createTab === "questions" && qMode === "import" && (
      <>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <button onClick={() => setQMode(qSource ? "edit" : "choose")} style={{ background: "none", border: `1px solid ${G.border}`, borderRadius: 8, color: G.muted, fontSize: 13, padding: "6px 12px", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
            {qSource ? t.backToQuestions : t.back}
          </button>
          {withCards && (
            <span style={{ fontSize: 13, color: G.gold }}>{t.reimportWarningWizard.replace("{n}", questions.length)}</span>
          )}
        </div>
        <ImportTab
          onImport={(importedQuestions) => {
            // Use the LARGEST option count — taking it from the first question
            // made later questions with more options (e.g. correct answer "E"
            // with numOptions 4) impossible to answer correctly
            const numOpts = Math.max(2, Math.min(6,
              Math.max(...importedQuestions.map(q => Object.keys(q.options || {}).length))));
            setNumQ(importedQuestions.length);
            setNumOptions(numOpts);
            setAnswerKey(importedQuestions.map(q => q.correctAnswer));
            setQuestions(importedQuestions);
            setShowQTexts(true);
            setQSource("imported"); setQMode("edit");
          }}
          lang={lang}
          G={G}
          css={css}
        />
        <div style={{ borderTop: `1px solid ${G.border}`, marginTop: 28, paddingTop: 28 }}>
          {/* Hopfälld från start: sidan blir annars lång under förhandsvisningen,
              och banken laddas först när den öppnas */}
          <button
            type="button"
            onClick={() => setShowBank(v => !v)}
            aria-expanded={showBank}
            style={{ ...css.heading, fontSize: 16, color: G.text, background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}
          >
            <span style={{ color: G.gold, fontSize: 13 }}>{showBank ? "▾" : "▸"}</span>
            {lang === "sv" ? "Från frågebank" : "From question bank"}
          </button>
          <p style={{ color: G.muted, fontSize: 14, margin: "6px 0 0 21px" }}>{lang === "sv" ? "Importera frågor du sparat tidigare." : "Import previously saved questions."}</p>
          {showBank && (
          <div style={{ marginTop: 16 }}>
          <BankImportTab
            onImport={(importedQuestions) => {
              const ALL_LABELS_LOCAL = ["A", "B", "C", "D", "E", "F"];
              // Compute the merged result in ONE pass so questions, answerKey,
              // numQ and numOptions stay in sync — the old per-state updates
              // could silently drop imported questions past numQ and leave
              // numOptions too small for the imported answers
              const mergedQuestions = [...questions];
              const mergedKey = [...answerKey];
              let insertIdx = 0;
              let neededOptions = numOptions;
              for (const iq of importedQuestions) {
                while (insertIdx < mergedQuestions.length && mergedQuestions[insertIdx]?.text?.trim()) insertIdx++;
                const q = { text: iq.text, rationale: iq.rationale, options: iq.options,
                            ...(iq.images?.length ? { images: iq.images } : {}) };
                const keyIdx = Math.max(0, ALL_LABELS_LOCAL.indexOf(iq.correctAnswerLabel || "A"));
                if (insertIdx < mergedQuestions.length) {
                  mergedQuestions[insertIdx] = q;
                  mergedKey[insertIdx] = keyIdx;
                } else {
                  mergedQuestions.push(q);
                  mergedKey.push(keyIdx);
                }
                neededOptions = Math.max(neededOptions, keyIdx + 1, Object.keys(iq.options || {}).length);
                insertIdx++;
              }
              setQuestions(mergedQuestions);
              setAnswerKey(mergedKey);
              setNumQ(mergedQuestions.length);
              setNumOptions(Math.min(6, neededOptions));
              setShowQTexts(true);
              setQSource("imported"); setQMode("edit");
            }}
            selectedCourseInstanceId={selectedCourseInstanceId}
            myCourseInstances={myCourseInstances}
            lang={lang}
            G={G}
            css={css}
          />
          </div>
          )}
        </div>
      </>
    )}

    {/* Inställningsfliken är första steget, inte sista: den primära knappen
        för vidare i flödet. Låg "Skapa tRAT" kvar här skulle det gå att
        skapa en tRAT med det orörda standardfacit (15 frågor, alla A) utan
        att ha sett facit över huvud taget. */}
    {createTab === "settings" && (
      <>
        <button style={{ ...css.btnPrimary, width: "100%", marginTop: 8 }} onClick={() => setCreateTab("questions")}>
          {t.nextImportQuestions}
        </button>
        <button style={css.btnSecondary} onClick={onBack}>{t.cancel}</button>
      </>
    )}

    {/* Skapa-raden är fäst längst ned i fönstret så den syns oavsett hur
        många frågor man scrollat förbi. Bara i redigeringsläget — sista steget. */}
    {createTab === "questions" && qMode === "edit" && (
      <div style={{ position: "sticky", bottom: 0, background: G.bg, borderTop: `1px solid ${G.border}`, padding: "12px 0 8px", marginTop: 16, display: "flex", gap: 10, alignItems: "center", zIndex: 5 }}>
        <button style={{ ...css.btnSecondary, marginTop: 0, width: "auto", minWidth: 120 }} onClick={onBack}>{t.cancel}</button>
        <button style={{ ...css.btnPrimary, flex: 1, marginTop: 0 }} onClick={createSession} disabled={saving}>{saving ? t.creating : t.generateCodes}</button>
      </div>
    )}
  </>
)}

{tab === "sessions" && (
  <TeacherSessionList
    sessions={mySessions}
    onRefresh={loadMySessions}
    courseLabelById={Object.fromEntries(myCourseInstances.map(inst => [inst.id, [inst.courses?.name, inst.name].filter(Boolean).join(" · ")]))}
    lang={lang}
    G={G}
    css={css}
  />
)}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
//  SESSION CREATED
// ─────────────────────────────────────────
function SessionCreated({ session, onDashboard, onBack, lang, setLang, theme, setTheme, G, css }) {
  const [copied, setCopied] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const { user } = useAuth();
  const t = T[lang];
  function copy(text, which) { copyToClipboard(text).then(() => { setCopied(which); setTimeout(() => setCopied(null), 2000); }); }
  return (
    <div style={css.page}>
      <div style={{ display: "flex", justifyContent: "center", padding: "32px 20px 40px" }}>
        <div style={css.card}>
          <h2 style={{ ...css.heading, fontSize: 28, marginBottom: 4 }}>{t.sessionReady}</h2>
          <p style={{ color: G.muted, fontSize: 14, marginBottom: 28 }}>
            {lang === "sv"
              ? `${session.numQ} frågor · ${session.numOptions} svarsalternativ · facit skyddat`
              : `${session.numQ} questions · ${session.numOptions} options · answer key secured`}
          </p>
          <div style={{ background: G.bg, border: `2px solid ${G.gold}44`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <div style={{ ...css.label, marginBottom: 12 }}>{t.shareWithGroups}</div>
            <div style={{ display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
              <div>
                <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 36, fontWeight: 500, color: G.gold, letterSpacing: "0.18em", marginBottom: 12 }}>{session.sessionId}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => copy(session.sessionId, "session")} style={{ flex: 1, background: copied === "session" ? G.accent + "22" : G.surface2, border: `1px solid ${copied === "session" ? G.accent : G.border}`, borderRadius: 8, color: copied === "session" ? G.accent : G.text, fontSize: 14, padding: "8px 16px", cursor: "pointer", transition: "all 0.2s" }}>{copied === "session" ? `✓ ${t.copied}` : `⎘ ${t.copy}`}</button>
                  <button onClick={() => setShowQR(!showQR)} style={{ background: showQR ? G.gold + "22" : G.surface2, border: `1px solid ${showQR ? G.gold : G.border}`, borderRadius: 8, color: showQR ? G.gold : G.text, fontSize: 14, padding: "8px 16px", cursor: "pointer" }}>{t.qrCode}</button>
                </div>
              </div>
              {showQR && <QRDisplay text={joinUrl(session.sessionId)} G={G} size={200} />}
            </div>
            <div style={{ color: G.muted, fontSize: 13, marginTop: 10 }}>
              {session.config?.predefined_groups?.length > 0 ? t.sessionCodeHintPredefined : t.sessionCodeHint}
            </div>
          </div>
          {/* Group codes + code sheet — surfaced here, right after creation, so
              the teacher can print and hand them out immediately (previously
              only reachable via a hidden dashboard menu). */}
          {session.config?.group_codes && Object.keys(session.config.group_codes).length > 0 && (
            <div style={{ background: G.bg, border: `2px solid ${G.gold}44`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
                <div>
                  <div style={{ ...css.label, marginBottom: 4 }}>{t.groupCodesTitle} · {Object.keys(session.config.group_codes).length}</div>
                  <div style={{ fontSize: 13, color: G.muted }}>{t.groupCodesDesc}</div>
                </div>
                <button
                  onClick={() => openCodeSheet(session.sessionTitle, session.sessionId, session.config.group_codes)}
                  style={{ ...css.btnPrimary, padding: "9px 18px", fontSize: 14, whiteSpace: "nowrap" }}>
                  ⎙ {lang === "sv" ? "Skriv ut kodblad" : "Print code sheet"}
                </button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
                {Object.entries(session.config.group_codes).map(([group, code]) => (
                  <div key={group} style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 10, padding: "9px 12px" }}>
                    <div style={{ fontSize: 12, color: G.muted, marginBottom: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group}</div>
                    <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 20, fontWeight: 700, color: G.gold, letterSpacing: "0.14em" }}>{code}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Dashboardkoden visas inte längre: läraren kommer till dashboarden via
              knappen, och den kräver LU-inloggning + roll. Koden lever kvar i URL:en
              för äldre sessioner utan ägare, men behöver inte hållas i handen. */}
          <div style={{ background: G.bg, border: `1px solid ${G.border}`, borderRadius: 12, padding: 20, marginBottom: 28 }}>
            <div style={{ ...css.label, marginBottom: 10 }}>{t.leaderboardCodeLabel}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, color: G.text, flex: 1, wordBreak: "break-all" }}>{window.location.origin}/?lb={session.leaderboardId}</div>
              <button onClick={() => copy(`${window.location.origin}/?lb=${session.leaderboardId}`, "lb")} style={{ background: copied === "lb" ? G.accent + "22" : G.surface2, border: `1px solid ${copied === "lb" ? G.accent : G.border}`, borderRadius: 8, color: copied === "lb" ? G.accent : G.text, fontSize: 14, padding: "7px 14px", cursor: "pointer", whiteSpace: "nowrap", transition: "all 0.2s" }}>{copied === "lb" ? `✓ ${t.copied}` : `⎘ ${t.copy}`}</button>
            </div>
            <div style={{ color: G.muted, fontSize: 13, marginTop: 8 }}>{t.leaderboardCodeHint}</div>
          </div>
          <button style={{ ...css.btnPrimary, width: "100%" }} onClick={onDashboard}>{t.openDashboard}</button>
          <button style={css.btnSecondary} onClick={onBack}>{t.createAnother}</button>
        </div>
      </div>
    </div>
  );
}

export { BankImportTab, TeacherSetup, SessionCreated };
